import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';
import { type Config } from 'tailwindcss';

export const SCREENS = Object.freeze({
  Mobile: 392,
  Tablet: 767,
  Desktop: 1365,
});

export const extendedTheme = {
  colors: {
    border: 'hsl(var(--border), <alpha-value>)',
    input: {
      DEFAULT: 'hsl(var(--input), <alpha-value>)',
      invalid: 'hsl(var(--input-invalid), <alpha-value>)',
    },
    ring: {
      DEFAULT: 'hsl(var(--ring), <alpha-value>)',
      invalid: 'hsl(var(--foreground-danger), <alpha-value>)',
    },
    background: 'hsl(var(--background), <alpha-value>)',
    foreground: {
      DEFAULT: 'hsl(var(--foreground), <alpha-value>)',
      danger: 'hsl(var(--foreground-danger), <alpha-value>)',
    },
    primary: {
      DEFAULT: 'hsl(var(--primary), <alpha-value>)',
      foreground: 'hsl(var(--primary-foreground), <alpha-value>)',
      dark: 'hsl(var(--primary-dark), <alpha-value>)',
      light: 'hsl(var(--primary-light), <alpha-value>)',
    },
    secondary: {
      DEFAULT: 'hsl(var(--secondary), <alpha-value>)',
      foreground: 'hsl(var(--secondary-foreground), <alpha-value>)',
      dark: 'hsl(var(--secondary-dark), <alpha-value>)',
      light: 'hsl(var(--secondary-light), <alpha-value>)',
    },
    destructive: {
      DEFAULT: 'hsl(var(--destructive), <alpha-value>)',
      foreground: 'hsl(var(--destructive-foreground), <alpha-value>)',
      dark: 'hsl(var(--destructive-dark), <alpha-value>)',
      light: 'hsl(var(--destructive-light), <alpha-value>)',
    },
    muted: {
      DEFAULT: 'hsl(var(--muted), <alpha-value>)',
      foreground: 'hsl(var(--muted-foreground), <alpha-value>)',
    },
    accent: {
      DEFAULT: 'hsl(var(--accent), <alpha-value>)',
      foreground: 'hsl(var(--accent-foreground), <alpha-value>)',
      dark: 'hsl(var(--accent-dark), <alpha-value>)',
      light: 'hsl(var(--accent-light), <alpha-value>)',
    },
    warning: {
      DEFAULT: 'hsl(var(--warning), <alpha-value>)',
      foreground: 'hsl(var(--warning-foreground), <alpha-value>)',
      dark: 'hsl(var(--warning-dark), <alpha-value>)',
      light: 'hsl(var(--warning-light), <alpha-value>)',
    },
    success: {
      DEFAULT: 'hsl(var(--success), <alpha-value>)',
      foreground: 'hsl(var(--success-foreground), <alpha-value>)',
      dark: 'hsl(var(--success-dark), <alpha-value>)',
      light: 'hsl(var(--success-light), <alpha-value>)',
    },
    info: {
      DEFAULT: 'hsl(var(--info), <alpha-value>)',
      foreground: 'hsl(var(--info-foreground), <alpha-value>)',
      dark: 'hsl(var(--info-dark), <alpha-value>)',
      light: 'hsl(var(--info-light), <alpha-value>)',
    },
    popover: {
      DEFAULT: 'hsl(var(--popover-foreground), <alpha-value>)',
      foreground: 'hsl(var(--popover), <alpha-value>)',
    },
    card: {
      DEFAULT: 'hsl(var(--card), <alpha-value>)',
      foreground: 'hsl(var(--card-foreground), <alpha-value>)',
    },
    green: {
      DEFAULT: 'hsl(var(--green), <alpha-value>)',
    },
    sidebar: {
      foreground: 'hsl(var(--sidebar-foreground), <alpha-value>)',
    },
    gray: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
  },
  borderColor: {
    DEFAULT: 'hsl(var(--border), <alpha-value>)',
  },
  borderRadius: {
    lg: 'var(--radius)',
    md: 'calc(var(--radius) - 2px)',
    sm: 'calc(var(--radius) - 4px)',
  },
  fontFamily: {
    sans: 'Merriweather Sans',
    mono: 'JetBrains Mono',
  },
  fontSize: {
    h1: ['2.986rem', { lineHeight: '120%', fontWeight: '400' }],
    h2: ['2.488rem', { lineHeight: '120%', fontWeight: '400' }],
    h3: ['2.074rem', { lineHeight: '120%', fontWeight: '400' }],
    h4: ['1.728rem', { lineHeight: '120%', fontWeight: '500' }],
    h5: ['1.44rem', { lineHeight: '130%', fontWeight: '500' }],
    h6: ['1.2rem', { lineHeight: '140%', fontWeight: '600' }],
    subtitle1: ['1rem', { lineHeight: '150%', fontWeight: '500' }],
    subtitle2: ['0.833rem', { lineHeight: '140%', fontWeight: '500' }],
    body1: ['1rem', { lineHeight: '150%', fontWeight: '300' }],
    body2: ['0.833rem', { lineHeight: '140%', fontWeight: '300' }],
    caption: ['0.694rem', { lineHeight: '140%', fontWeight: '300' }],
    overline: ['0.833rem', { lineHeight: '266%', fontWeight: '700' }],
    chip: ['0.833rem', { lineHeight: '135%', fontWeight: '300' }],

    /** 32px size / 36px high / normal */
    'body-2xl': ['2rem', { lineHeight: '2.25rem' }],
    /** 28px size / 36px high / normal */
    'body-xl': ['1.75rem', { lineHeight: '2.25rem' }],
    /** 24px size / 32px high / normal */
    'body-lg': ['1.5rem', { lineHeight: '2rem' }],
    /** 20px size / 28px high / normal */
    'body-md': ['1.25rem', { lineHeight: '1.75rem' }],
    /** 16px size / 20px high / normal */
    'body-sm': ['1rem', { lineHeight: '1.25rem' }],
    /** 14px size / 18px high / normal */
    'body-xs': ['0.875rem', { lineHeight: '1.125rem' }],
    /** 12px size / 16px high / normal */
    'body-2xs': ['0.75rem', { lineHeight: '1rem' }],

    /** 12px size / 16px high / bold */
    button: ['0.75rem', { lineHeight: '1rem', fontWeight: '700' }],
    /** 13.33 size / 24 px high / bold */
    'button-medium': ['0.833rem', { lineHeight: '1.5rem', fontWeight: '700' }],
    /** 11.1px size / 22px high / semibold */
    'button-small': ['0.694rem', { lineHeight: '1.375rem', fontWeight: '600' }],
  },
  opacity: {
    '4': '.04',
    '12': '.12',
    '30': '.30',
    '38': '.38',
    '56': '.56',
  },
  gridTemplateColumns: {
    sidebar: '56px auto', // for admin sidebar layout. adds grid-cols-sidebar class
    sidenav: '300px auto', // for sidebar layout. adds grid-cols-sidenav class
  },
  gridTemplateRows: {
    header: '80px auto', // for the navbar layout. adds grid-rows-header class
  },
  screens: {
    mobile: `${SCREENS.Mobile}px`,
    tablet: `${SCREENS.Tablet}px`,
    desktop: `${SCREENS.Desktop}px`,
  },
  backgroundImage: {
    'full-bg-1': 'url(/img/bg-image-1.jpg)',
    'full-bg-2': 'url(https://via.placeholder.com/1366x768)',
    // 'full-bg-2': 'url(/images/full-bg-2.jpg)',
    // 'full-bg-3': 'url(/images/full-bg-3.jpg)',
  },
  animation: {
    'slide-down': 'slideDown 200ms ease-out',
    'slide-up': 'slideUp 200ms ease-out',
  },
  keyframes: {
    slideDown: {
      from: { height: '0' },
      to: { height: 'var(--radix-accordion-content-height)' },
    },
    slideUp: {
      from: { height: 'var(--radix-accordion-content-height)' },
      to: { height: '0' },
    },
  },
} satisfies Config['theme'];

function formatColors() {
  const colors = [];
  for (const [key, color] of Object.entries(extendedTheme.colors)) {
    if (typeof color === 'string') {
      colors.push(key);
    } else {
      const colorGroup = Object.keys(color).map((subKey) => (subKey === 'DEFAULT' ? '' : subKey));
      colors.push({ [key]: colorGroup });
    }
  }
  return colors;
}

const customTwMerge = extendTailwindMerge<string, string>({
  extend: {
    theme: {
      colors: formatColors(),
      borderRadius: Object.keys(extendedTheme.borderRadius),
    },
    classGroups: {
      'font-size': [
        {
          text: Object.keys(extendedTheme.fontSize),
        },
      ],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return customTwMerge(clsx(inputs));
}
